import { StashColors } from '../../types/colors';

/**
 * @see: https://wunnle.com/dynamic-text-color-based-on-background
 */

function getRGB(c: string): number {
  const parsed = parseInt(c, 16);

  return Number.isNaN(parsed) ? 0 : parsed;
}

function getsRGB(c: string): number {
  const r = getRGB(c) / 255;

  return r <= 0.03928 ? r / 12.92 : Math.pow((r + 0.055) / 1.055, 2.4);
}

function getLuminance(hexColor: string): number {
  return (
    0.2126 * getsRGB(hexColor.slice(1, 3)) +
    0.7152 * getsRGB(hexColor.slice(3, 5)) +
    0.0722 * getsRGB(hexColor.slice(-2))
  );
}

function getContrast(foreground: string, background: string): number {
  const L1 = getLuminance(foreground);
  const L2 = getLuminance(background);

  return (Math.max(L1, L2) + 0.05) / (Math.min(L1, L2) + 0.05);
}

function getStashColorValueFromCssVar(color: string) {
  return getComputedStyle(document.documentElement).getPropertyValue(`--color-${color}`).trim();
}

function getStashColor(color: string) {
  // String enum values at runtime; widen to string[] so .includes(color) is type-safe (code-patterns: avoid assertions where possible).
  const stashColors: string[] = Object.values(StashColors);
  const exactMatch = stashColors.includes(color);

  if (exactMatch) {
    return getStashColorValueFromCssVar(color);
  }

  // check if a primary color group is provided, eg. 'blue' in 'blue-100'
  const partialMatch = stashColors.find((stashColor) => {
    return stashColor.split('-')[0] === color;
  });

  if (partialMatch) {
    return getStashColorValueFromCssVar(`${color}-500`);
  }

  return '';
}

function normalizeHexValue(hexValue: string) {
  if (hexValue.startsWith('#')) {
    return hexValue;
  }

  return `#${hexValue}`;
}

function getContrastingHexValue(colorHexValue: string) {
  const darkTextColor = '#27282A'; // ice-900
  const lightTextColor = '#FFFFFF'; // white
  const lightTextContrast = getContrast(colorHexValue, lightTextColor);
  const darkTextContrast = getContrast(colorHexValue, darkTextColor);

  return lightTextContrast > darkTextContrast ? lightTextColor : darkTextColor;
}

const DEFAULT_CONTRAST_COLOR = '#27282A';

/**
 * Returns a contrasting text color (light or dark) for a given background color.
 * Accepts hex values or Stash color names (resolved via CSS variables in the browser).
 * In SSR or when the color is invalid, returns the default contrast color (ice-900).
 *
 * @param color - Hex string (e.g. #fff or abc123) or Stash color name (e.g. blue-500)
 * @returns Hex string for the contrasting text color
 */
export default function getContrastingTextColor(color: string) {
  if (!color?.trim()) {
    return DEFAULT_CONTRAST_COLOR;
  }

  const hexRegex = new RegExp('^#?(([A-Fa-f0-9]{3})|([A-Fa-f0-9]{6}))$');

  if (hexRegex.test(color)) {
    const normalizedHexValue = normalizeHexValue(color);
    return getContrastingHexValue(normalizedHexValue);
  }

  // Stash color names require getComputedStyle(document.documentElement), which is unavailable in SSR (Node).
  if (typeof document === 'undefined') {
    return DEFAULT_CONTRAST_COLOR;
  }

  const stashColor = getStashColor(color);

  if (stashColor && hexRegex.test(stashColor.trim())) {
    const normalized = normalizeHexValue(stashColor.trim());
    return getContrastingHexValue(normalized);
  }

  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.warn(`getContrastingTextColor received an invalid color value: ${color}`);
  }

  return DEFAULT_CONTRAST_COLOR;
}
